import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

let locId = localStorage.getItem("86d_loc");
export const setLocationHeader = (id) => {
  locId = id;
  if (id) localStorage.setItem("86d_loc", id);
};
export const getLocationId = () => locId;

export const localDate = (d = new Date()) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export const writes = { last: 0 };
api.interceptors.request.use((c) => {
  if (!/^(get|head|options)$/i.test(c.method || "get")) writes.last = Date.now();
  if (locId && !c.headers["X-Location-Id"]) c.headers["X-Location-Id"] = locId;
  c.headers["X-Local-Date"] = localDate();
  c.headers["X-Requested-With"] = "86d";
  c.headers["X-Request-ID"] = Math.random().toString(36).slice(2, 14);
  return c;
});

let reauthFn = null;
export const setReauthHandler = (fn) => { reauthFn = fn; };

let refreshing = null;
let accessPing = 0;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const det = err.response?.data?.detail;
    if (err.response?.status === 403 && typeof det === "string" && /upgrade_required|permission|access to this|view-only/.test(det) && Date.now() - accessPing > 10000) {
      accessPing = Date.now();
      window.dispatchEvent(new Event("access-changed"));
    }
    if (err.response?.status === 403 && err.response?.data?.detail === "reauth_required" && reauthFn && !cfg._reauthed) {
      if (await reauthFn()) { cfg._reauthed = true; return api(cfg); }
    }
    if (err.response?.status === 403 && err.response?.data?.detail === "mfa_setup_required") window.dispatchEvent(new Event("mfa-setup-required"));
    if (err.response?.status === 401 && !cfg._retry && !String(cfg.url).includes("/auth/")) {
      cfg._retry = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => (refreshing = null));
      try {
        await refreshing;
        return api(cfg);
      } catch (_) {
        window.dispatchEvent(new Event("session-expired"));
      }
    }
    return Promise.reject(err);
  }
);

export const errRef = (e) => e?.config?.headers?.["X-Request-ID"] || e?.response?.data?.ref || e?.response?.data?.detail?.ref || null;

export function errKind(e) {
  const s = e?.response?.status;
  if (!e?.response) return "unavailable";
  if (s === 401) return "expired";
  if (s === 403) return "denied";
  if (s >= 500) return "unavailable";
  return "error";
}

const itemText = (x) => (x == null ? "" : typeof x === "string" ? x : typeof x === "number" ? String(x) : Array.isArray(x) ? x.map(itemText).filter(Boolean).join("; ")
  : x.msg || x.message || x.label || x.text || x.reason || x.name || x.detail || "");

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) {
    const st = e?.response?.status;
    if (!e?.response) return "No connection. Nothing was saved. Check your connection and try again.";
    return st === 413 ? "That file or request is too large." : st >= 500 ? "Something went wrong on our side. Nothing was saved. Please try again." : "Something went wrong. Please try again.";
  }
  if (typeof d === "string") {
    if (d === "mfa_setup_required") return "Set up two-step verification to continue.";
    if (d === "reauth_required") return "Please confirm it's you to continue.";
    const planName = { essential: "Essential", ops_pro: "Operations Pro", group: "Group" }[d.split(":")[1]] || "a higher";
    if (d.startsWith("permission_and_plan_required")) return `This needs the ${planName} plan and permission for this area. Ask the owner if you need it.`;
    return d.startsWith("upgrade_required") ? `This feature needs the ${planName} plan.` : d;
  }
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? `${(x.loc || []).filter((p) => p !== "body").join(" › ")}${x.loc?.length > 1 ? ": " : ""}${x.msg}` : itemText(x))).filter(Boolean).join(" ") || "Some fields are invalid.";
  const head = d.msg || d.message || "";
  const list = d.blocking || d.errors || d.warnings;
  const extra = Array.isArray(list) ? itemText(list) : list && typeof list === "object" ? Object.values(list).map(itemText).filter(Boolean).join("; ") : "";
  return [head, extra].filter(Boolean).join(" ") || "The request couldn't be completed. Nothing was saved.";
}

// Offline outbox — never silently lose entries
const OB = "86d_outbox";
const listeners = new Set();
export const outbox = {
  list: () => JSON.parse(localStorage.getItem(OB) || "[]"),
  save: (items) => {
    localStorage.setItem(OB, JSON.stringify(items));
    listeners.forEach((fn) => fn(items.length));
  },
  subscribe: (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export async function saveRecord(path, body, method = "post") {
  try {
    const { data } = await api[method](path, body);
    return { data, queued: false };
  } catch (e) {
    if (!e.response) {
      outbox.save([...outbox.list(), { path, body, method, loc: locId, ts: Date.now() }]);
      return { queued: true };
    }
    throw e;
  }
}

let flushing = false;
export async function flushOutbox() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  let items = outbox.list();
  for (const it of [...items]) {
    try {
      await api[it.method](it.path, it.body, { headers: { "X-Location-Id": it.loc } });
      items = items.filter((x) => x.ts !== it.ts);
    } catch (e) {
      if (e.response && e.response.status < 500) items = items.filter((x) => x.ts !== it.ts);
      else break;
    }
  }
  outbox.save(items);
  flushing = false;
}
window.addEventListener("online", flushOutbox);
setInterval(flushOutbox, 30000);
